import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface VolunteerApplicationData {
  full_name: string;
  email: string;
  phone?: string;
  city?: string;
  availability: string;
  experience: string;
  motivation: string;
  skills: string[];
  previous_volunteer_experience?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  dietary_restrictions?: string;
  t_shirt_size?: string;
  transportation?: string;
  accommodation_needed?: boolean;
}

interface VolunteerApplicationFormProps {
  eventId?: string;
  onSuccess?: () => void;
}

export const VolunteerApplicationForm = ({ eventId, onSuccess }: VolunteerApplicationFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const { toast } = useToast();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<VolunteerApplicationData>({
    defaultValues: {
      skills: [],
      accommodation_needed: false
    }
  });

  const availableSkills = [
    "Registration & Check-in",
    "Technical Support",
    "Social Media",
    "Photography/Videography", 
    "Event Setup/Breakdown",
    "Guest Relations",
    "Translation/Interpretation",
    "Marketing & Promotion",
    "Logistics & Coordination",
    "Catering Support"
  ];

  const handleSkillChange = (skill: string, checked: boolean) => {
    const updatedSkills = checked 
      ? [...selectedSkills, skill]
      : selectedSkills.filter(s => s !== skill);
    
    setSelectedSkills(updatedSkills);
    setValue("skills", updatedSkills);
  };

  const onSubmit = async (data: VolunteerApplicationData) => {
    // availability is a Select that only calls setValue — validate manually
    const availability = watch("availability");
    if (!availability) {
      toast({ title: "Please select your availability", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const applicationData = {
        ...data,
        availability,
        ...(user ? { user_id: user.id } : {}),
        event_id: eventId,
        skills: selectedSkills
      };

      const { error } = await (supabase as any)
        .from('volunteer_applications')
        .insert(applicationData);

      if (error) {
        throw error;
      }

      toast({
        title: "Application submitted!",
        description: "Your volunteer application has been submitted for review."
      });
      
      reset();
      setSelectedSkills([]);
      onSuccess?.();
    } catch (error) {
      console.error('Error submitting volunteer application:', error);
      const msg = (error as any)?.message || (error as any)?.error_description || String(error);
      toast({
        title: "Error",
        description: `Failed to submit application: ${msg}`,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Volunteer Application</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                {...register("full_name", { required: "Full name is required" })}
                className={errors.full_name ? "border-destructive" : ""}
              />
              {errors.full_name && (
                <p className="text-sm text-destructive mt-1">{errors.full_name.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                {...register("email", { required: "Email is required" })}
                className={errors.email ? "border-destructive" : ""}
              />
              {errors.email && (
                <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                {...register("phone")}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            
            <div>
              <Label htmlFor="city">Current City</Label>
              <Input
                id="city"
                {...register("city")}
                placeholder="Your current city"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="availability">Availability *</Label>
            <Select onValueChange={(value) => setValue("availability", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select your availability" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full-event">Full Event</SelectItem>
                <SelectItem value="specific-days">Specific Days Only</SelectItem>
                <SelectItem value="mornings">Mornings Only</SelectItem>
                <SelectItem value="afternoons">Afternoons Only</SelectItem>
                <SelectItem value="evenings">Evenings Only</SelectItem>
                <SelectItem value="flexible">Flexible</SelectItem>
              </SelectContent>
            </Select>
            {errors.availability && (
              <p className="text-sm text-destructive mt-1">{errors.availability.message}</p>
            )}
          </div>

          <div>
            <Label>Areas of Interest *</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {availableSkills.map((skill) => (
                <div key={skill} className="flex items-center space-x-2">
                  <Checkbox
                    id={skill}
                    checked={selectedSkills.includes(skill)}
                    onCheckedChange={(checked) => handleSkillChange(skill, checked as boolean)}
                  />
                  <Label htmlFor={skill} className="text-sm">{skill}</Label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="motivation">Why do you want to volunteer? *</Label>
            <Textarea
              id="motivation"
              {...register("motivation", { required: "Please tell us your motivation" })}
              className={errors.motivation ? "border-destructive" : ""}
              rows={3}
            />
            {errors.motivation && (
              <p className="text-sm text-destructive mt-1">{errors.motivation.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="experience">Relevant Experience *</Label>
            <Textarea
              id="experience"
              {...register("experience", { required: "Please describe your relevant experience" })}
              className={errors.experience ? "border-destructive" : ""}
              rows={3}
              placeholder="Tell us about any relevant work, volunteer, or personal experience"
            />
            {errors.experience && (
              <p className="text-sm text-destructive mt-1">{errors.experience.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="previous_volunteer_experience">Previous Volunteer Experience</Label>
            <Textarea
              id="previous_volunteer_experience"
              {...register("previous_volunteer_experience")}
              rows={2}
              placeholder="Any previous volunteer experience at events or organizations?"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="emergency_contact_name">Emergency Contact Name</Label>
              <Input
                id="emergency_contact_name"
                {...register("emergency_contact_name")}
              />
            </div>
            
            <div>
              <Label htmlFor="emergency_contact_phone">Emergency Contact Phone</Label>
              <Input
                id="emergency_contact_phone"
                type="tel"
                {...register("emergency_contact_phone")}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="t_shirt_size">T-Shirt Size</Label>
              <Select onValueChange={(value) => setValue("t_shirt_size", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="xs">XS</SelectItem>
                  <SelectItem value="s">S</SelectItem>
                  <SelectItem value="m">M</SelectItem>
                  <SelectItem value="l">L</SelectItem>
                  <SelectItem value="xl">XL</SelectItem>
                  <SelectItem value="xxl">XXL</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="dietary_restrictions">Dietary Restrictions</Label>
              <Input
                id="dietary_restrictions"
                {...register("dietary_restrictions")}
                placeholder="Any dietary restrictions or allergies"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="transportation">Transportation</Label>
            <Textarea
              id="transportation"
              {...register("transportation")}
              rows={2}
              placeholder="How will you get to the event? Do you need any transportation assistance?"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="accommodation_needed"
              {...register("accommodation_needed")}
            />
            <Label htmlFor="accommodation_needed">I need accommodation assistance</Label>
          </div>

          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? "Submitting..." : "Submit Application"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};